import { keepPreviousData } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { type ReactNode, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Dialog,
  Pagination,
  Skeleton,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tr,
  useToast,
} from "@rumbledirect/ui-v2";
import { RequirePermission } from "@/currentUser/RequirePermission";
import { useGetAdminSites } from "@/lib/api/generated/site/site";
import type { Site } from "@/sites/site";
import { toSiteInput } from "@/sites/siteForm";
import { SiteSheet } from "@/sites/SiteSheet";
import { useCreateSite } from "@/sites/useCreateSite";
import { useDeleteSite } from "@/sites/useDeleteSite";
import { useUpdateSite } from "@/sites/useUpdateSite";
import { TopbarActions } from "@/shell/TopbarActions";

const CREATE_PERMISSION = "admin_site_create";
const UPDATE_PERMISSION = "admin_site_update";
const DELETE_PERMISSION = "admin_site_delete";

const ITEMS_PER_PAGE = 20;
const SKELETON_ROW_KEYS = Array.from(
  { length: 5 },
  (_, index) => `site-skeleton-${index}`,
);

interface SitesSearch {
  readonly page: number;
}

export const Route = createFileRoute("/_authenticated/sites")({
  validateSearch: (search: Record<string, unknown>): SitesSearch => {
    const page = Number(search.page);
    return { page: Number.isInteger(page) && page >= 1 ? page : 1 };
  },
  component: SitesPage,
});

function SitesPage(): ReactNode {
  const { page } = Route.useSearch();
  const navigate = Route.useNavigate();
  const toast = useToast();

  const { data, isPending, isPlaceholderData, isError, refetch } =
    useGetAdminSites(
      { page },
      { query: { placeholderData: keepPreviousData } },
    );

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Site | undefined>(undefined);
  const [pendingDelete, setPendingDelete] = useState<Site | undefined>(
    undefined,
  );

  const createMutation = useCreateSite();
  const updateMutation = useUpdateSite(editing?.id ?? "");
  const deleteMutation = useDeleteSite();

  const totalItems = data?.totalItems ?? 0;
  const members = data?.member;

  return (
    <div className="flex flex-col gap-5">
      <TopbarActions>
        <RequirePermission permission={CREATE_PERMISSION}>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" aria-hidden="true" />
            Nieuwe locatie
          </Button>
        </RequirePermission>
      </TopbarActions>

      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Locaties</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Beheer de locaties waaronder je voertuigen kunt groeperen.
        </p>
      </div>

      <Card className="overflow-hidden">
        <Table>
          <THead>
            <Tr>
              <Th>Naam</Th>
              <Th className="w-28">Status</Th>
              <Th className="w-24 text-right">
                <span className="sr-only">Acties</span>
              </Th>
            </Tr>
          </THead>
          <TBody
            aria-busy={isPlaceholderData}
            className={isPlaceholderData ? "opacity-60" : undefined}
          >
            <SiteRows
              isPending={isPending}
              isError={isError}
              members={members}
              canUpdate={UPDATE_PERMISSION}
              canDelete={DELETE_PERMISSION}
              onEdit={setEditing}
              onDelete={setPendingDelete}
              onRetry={() => void refetch()}
            />
          </TBody>
        </Table>
      </Card>

      {totalItems > ITEMS_PER_PAGE && (
        <Pagination
          page={page}
          pageSize={ITEMS_PER_PAGE}
          totalItems={totalItems}
          status={isPending ? "pending" : isError ? "error" : "ready"}
          onPageChange={(next) =>
            void navigate({ search: (prev) => ({ ...prev, page: next }) })
          }
        />
      )}

      <SiteSheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        isSubmitting={createMutation.isPending}
        onSubmit={(values) => {
          createMutation.mutate(toSiteInput(values), {
            onSuccess: () => setCreateOpen(false),
          });
        }}
      />

      <SiteSheet
        open={editing !== undefined}
        onOpenChange={(open) => {
          if (!open) setEditing(undefined);
        }}
        site={editing}
        isSubmitting={updateMutation.isPending}
        onSubmit={(values) => {
          if (!editing?.id) {
            toast.error("Bijwerken is mislukt. Probeer het opnieuw.");
            setEditing(undefined);
            return;
          }
          updateMutation.mutate(toSiteInput(values), {
            onSuccess: () => setEditing(undefined),
          });
        }}
      />

      <Dialog
        open={pendingDelete !== undefined}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(undefined);
        }}
        title="Locatie verwijderen"
        description={
          pendingDelete
            ? `Weet je zeker dat je "${pendingDelete.name}" wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.`
            : undefined
        }
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setPendingDelete(undefined)}
              disabled={deleteMutation.isPending}
            >
              Annuleren
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => {
                const id = pendingDelete?.id;
                if (!id) {
                  toast.error("Verwijderen is mislukt. Probeer het opnieuw.");
                  return;
                }
                deleteMutation.mutate(id, {
                  onSuccess: () => setPendingDelete(undefined),
                });
              }}
            >
              <Trash2 className="size-4" aria-hidden="true" />
              Verwijderen
            </Button>
          </>
        }
      />
    </div>
  );
}

function SiteRows({
  isPending,
  isError,
  members,
  canUpdate,
  canDelete,
  onEdit,
  onDelete,
  onRetry,
}: {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly members: readonly Site[] | undefined;
  readonly canUpdate: string;
  readonly canDelete: string;
  readonly onEdit: (site: Site) => void;
  readonly onDelete: (site: Site) => void;
  readonly onRetry: () => void;
}): ReactNode {
  if (isPending) {
    return SKELETON_ROW_KEYS.map((key) => (
      <Tr key={key}>
        <Td>
          <Skeleton className="h-4 w-32" />
        </Td>
        <Td>
          <Skeleton className="h-5 w-16 rounded-full" />
        </Td>
        <Td>
          <div className="flex justify-end gap-1">
            <Skeleton className="h-8 w-8" />
            <Skeleton className="h-8 w-8" />
          </div>
        </Td>
      </Tr>
    ));
  }

  if (isError) {
    return (
      <Tr>
        <Td colSpan={3} className="py-10 text-center">
          <div className="space-y-3">
            <p className="text-muted-foreground">
              We konden de locaties niet laden.
            </p>
            <Button variant="outline" size="sm" onClick={onRetry}>
              Opnieuw proberen
            </Button>
          </div>
        </Td>
      </Tr>
    );
  }

  if (members === undefined || members.length === 0) {
    return (
      <Tr>
        <Td colSpan={3} className="py-10 text-center text-muted-foreground">
          Nog geen locaties.
        </Td>
      </Tr>
    );
  }

  return members.map((site) => (
    <Tr key={site.id}>
      <Td className="font-medium">{site.name}</Td>
      <Td>
        {site.active ? (
          <Badge variant="success" size="sm">
            Actief
          </Badge>
        ) : (
          <Badge variant="neutral" size="sm">
            Inactief
          </Badge>
        )}
      </Td>
      <Td>
        <div className="flex justify-end gap-1">
          <RequirePermission permission={canUpdate}>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={`${site.name} bewerken`}
              onClick={() => onEdit(site)}
            >
              <Pencil className="size-4" aria-hidden="true" />
            </Button>
          </RequirePermission>
          <RequirePermission permission={canDelete}>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={`${site.name} verwijderen`}
              onClick={() => onDelete(site)}
            >
              <Trash2 className="size-4" aria-hidden="true" />
            </Button>
          </RequirePermission>
        </div>
      </Td>
    </Tr>
  ));
}
